import styles from '@/components/Avatar.module.css';

/**
 * A round identity avatar: the uploaded photo when there is one, otherwise
 * initials on a colour derived from the identity.
 *
 * The colour is derived rather than stored, and derived from the *identifier*
 * rather than the name. Two people called Alex get different colours, and a
 * person who changes their display name keeps the colour everyone already
 * associates with them. Deriving from the name would reshuffle the whole list
 * whenever somebody edited their profile.
 *
 * Initials are taken by grapheme-ish segmentation rather than `charAt`, because
 * `"😀"[0]` is half a surrogate pair and an emoji-only name would render a broken
 * glyph. Scripts without a Latin notion of initials fall back to the first
 * character of the name, which is what a reader of that script expects anyway.
 */

/** Palette chosen for legibility of white text at 14px, not copied from anywhere. */
const SWATCHES = [
  '#d1495b',
  '#c9732b',
  '#b08900',
  '#4c956c',
  '#2f8f9d',
  '#3d6bb3',
  '#6a4c93',
  '#b5476e',
] as const;

function swatchFor(seed: string): string {
  // FNV-1a: a few instructions, stable across runs, and not a cryptographic
  // claim. It only has to spread a handful of identifiers over eight buckets.
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return SWATCHES[Math.abs(hash) % SWATCHES.length] ?? SWATCHES[0];
}

function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return '?';

  const words = trimmed.split(/\s+/).slice(0, 2);
  const letters = words
    .map((word) => Array.from(word)[0] ?? '')
    .join('')
    .toUpperCase();
  return letters === '' ? '?' : letters;
}

export interface AvatarProps {
  /** The stable identifier the colour is derived from. */
  id: string;
  name: string;
  /** A resolved image URL, when one has been fetched and authorized. */
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  /** A saved-messages or channel glyph instead of initials, where it applies. */
  glyph?: string;
}

export function Avatar({ id, name, photoUrl, size = 'md', glyph }: AvatarProps) {
  // The avatar repeats the name beside it in every place it is used, so it is
  // decorative here. Labelling it would make a screen reader say the name twice.
  if (photoUrl) {
    return (
      <img
        className={styles.avatar}
        data-size={size}
        src={photoUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <span
      className={styles.avatar}
      data-size={size}
      aria-hidden="true"
      style={{ background: swatchFor(id) }}
    >
      {glyph ?? initialsFor(name)}
    </span>
  );
}
